/**
 * Login — sign-in page shell. Composes:
 *   • StoryPanel  (left, story + brand + stats)
 *   • SignInCard  (right, forms + tabs + 2FA)
 *   • ForgotPasswordDialog (shared reset flow)
 * Data comes from `useLoginCms` which resolves brand + theme for the tenant.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, IndianRupee } from "lucide-react";
import { useLoginCms } from "./login/useLoginCms";
import { StoryPanel } from "./login/StoryPanel";
import { SignInCard } from "./login/SignInCard";
import { ForgotPasswordDialog } from "./login/ForgotPasswordDialog";

/** Detect installed-app mode (PWA, TWA, Capacitor) — no marketing links then. */
function useIsStandalone() {
  const [standalone, setStandalone] = useState(false);
  useEffect(() => {
    const q = window.matchMedia?.("(display-mode: standalone)");
    const check = () => setStandalone(!!(q?.matches || window.navigator.standalone));
    check();
    q?.addEventListener?.("change", check);
    return () => q?.removeEventListener?.("change", check);
  }, []);
  return standalone;
}

export default function Login({ mode = "customer" }) {
  const isStaff = mode === "staff";
  const isStandalone = useIsStandalone();
  const { brand, customerTheme, staffTheme } = useLoginCms();

  // Lifted email state so the forgot-password dialog can pre-fill it and the
  // reset-success callback can auto-populate the login form.
  const [email, setEmail] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);

  const backLinkStyle = isStaff
    ? { borderColor: "rgba(255,255,255,0.15)", color: staffTheme.muted_text, background: "rgba(255,255,255,0.03)" }
    : { borderColor: `${customerTheme.accent}33`, color: customerTheme.accent, background: "#fff" };

  return (
    <div
      className="relative grid min-h-screen grid-cols-1 overflow-hidden md:grid-cols-[1.1fr_1fr]"
      style={{
        backgroundColor: isStaff ? staffTheme.bg : customerTheme.panel_bg,
        color: isStaff ? staffTheme.text : "inherit",
      }}
      data-testid={isStaff ? "login-staff" : "login-customer"}
    >
      <AmbientOrbs isStaff={isStaff} customerTheme={customerTheme} staffTheme={staffTheme} />

      <StoryPanel isStaff={isStaff} brand={brand} customerTheme={customerTheme} staffTheme={staffTheme} isStandalone={isStandalone} />

      <div className="relative z-10 flex flex-col items-center justify-center px-6 py-12">
        {!isStandalone && (
          <div className="mb-4 w-full max-w-md">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors"
              style={backLinkStyle}
              data-testid="link-back-home"
            >
              <ArrowRight size={11} className="rotate-180" />
              Back to home
            </Link>
          </div>
        )}

        <SignInCard
          isStaff={isStaff}
          brand={brand}
          customerTheme={customerTheme}
          staffTheme={staffTheme}
          onOpenForgot={() => setForgotOpen(true)}
          email={email}
          setEmail={setEmail}
        />

        <FooterLinks isStaff={isStaff} brand={brand} staffTheme={staffTheme} />
      </div>

      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        initialEmail={email}
        onResetSuccess={(resetEmail) => setEmail(resetEmail)}
      />
    </div>
  );
}

function AmbientOrbs({ isStaff, customerTheme, staffTheme }) {
  if (isStaff) {
    return (
      <>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)",
          }}
        />
        <div aria-hidden className="pointer-events-none absolute -left-40 top-1/4 h-96 w-96 rounded-full opacity-30 blur-3xl" style={{ backgroundColor: staffTheme.orb_1 }} />
        <div aria-hidden className="pointer-events-none absolute -right-40 bottom-0 h-96 w-96 rounded-full opacity-20 blur-3xl" style={{ backgroundColor: staffTheme.orb_2 }} />
      </>
    );
  }
  return (
    <>
      <div aria-hidden className="pointer-events-none absolute -left-40 -top-40 h-[32rem] w-[32rem] rounded-full opacity-30 blur-3xl" style={{ backgroundColor: customerTheme.gradient_from }} />
      <div aria-hidden className="pointer-events-none absolute -right-24 top-1/3 h-96 w-96 rounded-full opacity-40 blur-3xl" style={{ backgroundColor: customerTheme.gradient_to }} />
    </>
  );
}

function FooterLinks({ isStaff, brand, staffTheme }) {
  return (
    <div className="mt-6 text-center text-[11px]" style={{ color: isStaff ? staffTheme.muted_text : "#94A3B8" }}>
      <div className="flex items-center justify-center gap-3">
        <Link to="/privacy" className="hover:underline">Privacy</Link>
        <span className="opacity-30">·</span>
        <Link to="/terms" className="hover:underline">Terms</Link>
        <span className="opacity-30">·</span>
        <Link to="/#contact" className="hover:underline">Support</Link>
      </div>
      <div className="mt-2 flex items-center justify-center gap-1 opacity-70">
        <IndianRupee size={10} /> © 2026 {brand.name || "FuelExpress"} — All rights reserved
      </div>
    </div>
  );
}
